import { useCallback, useEffect, useMemo, useState } from 'react';
import { listWorkspaces } from '../services/WorkspacesService';

/**
 * The person's workspaces (services/WorkspacesService.js), loaded once and on `reload()`:
 * - `workspaces`: the list ([] while loading);
 * - `available`: false where the gateway has no workspaces (the web app then shows none of it);
 * - `workspacesOf(projectId)`: the workspaces a project is in (a project may be in several), by name; `workspaceOf`
 *   the first, or null.
 */
export default function useWorkspaces({ enabled = true } = {}) {
  const [workspaces, setWorkspaces] = useState([]);
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const reload = useCallback(async() => {
    if (!enabled) { return; }
    setLoading(true);
    try {
      const list = await listWorkspaces();
      setAvailable(list !== null);
      setWorkspaces(list || []);
      setError(null);
    } catch (failed) {
      setError(failed);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => { reload(); }, [reload]);

  const byProject = useMemo(() => {
    const map = new Map();
    [...workspaces].sort((a, b) => String(a.name).localeCompare(String(b.name), 'en', { sensitivity: 'base' })).forEach((workspace) => (
      (workspace.projectIds || []).forEach((projectId) => map.set(projectId, [...(map.get(projectId) || []), workspace]))
    ));
    return map;
  }, [workspaces]);
  const workspacesOf = useCallback((projectId) => byProject.get(projectId) || [], [byProject]);
  const workspaceOf = useCallback((projectId) => workspacesOf(projectId)[0] || null, [workspacesOf]);

  return { workspaces, available, loading, error, reload, workspacesOf, workspaceOf };
}
